import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AiChatAttachButton, AiChatComposingAttachments, formatFileSize } from '@bliks-io/ui';
import { ChatInput } from '@/components/chat';
import { useVoiceInputSlot } from '@/components/chat/useVoiceInput';
import type { BliksyAttachmentDraft, UseBliksyAttachmentsResult } from './useBliksyAttachments';

interface BliksyChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled?: boolean;
  attachments: UseBliksyAttachmentsResult;
  /**
   * The stored transcript is still being fetched. Attaching and sending wait for
   * it; typing does not.
   */
  isHistoryLoading?: boolean;
  className?: string;
}

/**
 * Bliksy-branded chat input with voice recording and file attachments.
 * When recording, the input area is replaced by a recording bar overlay and the
 * attach button steps aside: a turn taken mid-recording would be missing the
 * words still being spoken.
 */
export function BliksyChatInput({
  value,
  onChange,
  onSend,
  disabled,
  attachments,
  isHistoryLoading,
  className
}: BliksyChatInputProps) {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { leftSlot, leftSlotExpanded } = useVoiceInputSlot({
    onChange,
    textareaRef,
    value,
    disabled
  });

  // Stable per language: the chip row memoises on this identity, and the panel
  // re-renders on every streamed token.
  const describeDraft = useCallback(
    (draft: BliksyAttachmentDraft): string => {
      if (draft.rejection) return t(draft.rejection.key, draft.rejection.values);
      if (draft.status === 'uploading') return t('chat.attachment.uploading');
      return formatFileSize(draft.size);
    },
    [t]
  );

  return (
    <>
      <AiChatComposingAttachments
        attachments={attachments.drafts}
        describe={describeDraft}
        onRemove={attachments.remove}
        removeLabel={t('chat.attachment.remove')}
      />

      <ChatInput
        value={value}
        onChange={onChange}
        onSend={onSend}
        disabled={disabled}
        canSend={
          (!!value.trim() || attachments.attachments.length > 0) &&
          !disabled &&
          !isHistoryLoading &&
          !attachments.isUploading
        }
        placeholder={t('bliksy.inputPlaceholder')}
        className={className}
        onPasteFiles={disabled || isHistoryLoading ? undefined : attachments.addFiles}
        leftSlot={
          leftSlotExpanded ? (
            leftSlot
          ) : (
            <>
              <AiChatAttachButton
                onFiles={attachments.addFiles}
                accept={attachments.accept}
                disabled={disabled || isHistoryLoading}
                label={t('chat.attachment.attach')}
              />
              {leftSlot}
            </>
          )
        }
        leftSlotExpanded={leftSlotExpanded}
        textareaRef={textareaRef}
      />
    </>
  );
}
